// Nov 2026, user: "Add a column for the Welding Robot where user just save his
// welding current running robot or user can map MSL for an item which is based
// on a field in the material master: SFG MSL. You just need to show that Qty of
// msl by calculating robot qty which set by the user ... ex: 10 robot day
// 2-11-2026 msl is 4000kg of migwire" + "do not allow user to change more then
// once in a day, only admin can change it".
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Robot, FloppyDisk, Trash, CircleNotch, DownloadSimple, Plus, MagnifyingGlass, CaretUpDown, Lock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { toast } from "@/components/ui/sonner";
import { api, registerError, qty } from "./registerApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/consumption-register`;

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

const ItemPicker = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const search = (value) => {
    if (timer.current) clearTimeout(timer.current);
    if (!value.trim()) { setResults([]); return; }
    setLoading(true);
    timer.current = setTimeout(() => {
      api.searchProducts(value.trim())
        .then((d) => setResults(d.products || d || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-9 w-full mt-1 flex items-center justify-between px-3 rounded-md border border-[#D0D5DD] bg-white text-sm text-left"
          data-testid="welding-item-picker"
        >
          <span className="text-[#98A2B3] flex items-center gap-2"><MagnifyingGlass size={13} /> Search the welding material...</span>
          <CaretUpDown size={13} className="text-[#667085] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput onValueChange={search} placeholder="MIG wire, flux..." data-testid="welding-item-search" />
          <CommandList>
            {loading && <div className="py-3 text-center text-xs text-[#667085]">Searching...</div>}
            {!loading && <CommandEmpty>No SAP material matches.</CommandEmpty>}
            <CommandGroup>
              {results.map((r) => (
                <CommandItem
                  key={r.product_id}
                  value={r.product_id}
                  onSelect={() => { onPick(r); setOpen(false); setResults([]); }}
                  data-testid={`welding-item-option-${r.product_id}`}
                >
                  <span className="font-mono text-xs font-bold text-[#101828] mr-2">{r.product_id}</span>
                  <span className="text-xs text-[#667085] truncate">{r.description}</span>
                  {r.uom && <span className="ml-auto text-[10px] font-mono text-[#0E7C86]">{r.uom}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const WeldingRobotPanel = ({ isAdmin, lockedSite = "" }) => {
  const [master, setMaster] = useState({ items: [], departments: [], sap_msl_field: null });
  const [report, setReport] = useState({ days: [] });
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const [siteId, setSiteId] = useState(lockedSite);
  const [entryDate, setEntryDate] = useState(today());
  const [robots, setRobots] = useState("");

  const [dateFrom, setDateFrom] = useState(daysAgo(14));
  const [dateTo, setDateTo] = useState(today());

  const [picked, setPicked] = useState(null);
  const [mslValue, setMslValue] = useState("");
  const [mslNote, setMslNote] = useState("");
  const [mslUom, setMslUom] = useState("");
  const [shift, setShift] = useState("A");

  const loadReport = useCallback(async (site, from, to) => {
    const data = await axios
      .get(`${API}/welding/report`, { params: { site_id: site || undefined, date_from: from, date_to: to } })
      .then((r) => r.data);
    setReport(data);
    setMaster((m) => ({ ...m, items: data.items ?? m.items, departments: data.departments ?? m.departments, sap_msl_field: data.sap_msl_field }));
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const m = await axios.get(`${API}/welding/master`, { params: { site_id: lockedSite || undefined } }).then((r) => r.data);
        setMaster(m);
        const firstSite = lockedSite || (m.departments || [])[0]?.site_id || "";
        setSiteId(firstSite);
        await loadReport(firstSite, daysAgo(14), today());
      } catch (e) {
        toast.error(registerError(e).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadReport, lockedSite]);

  const saveRobots = async () => {
    setWorking(true);
    try {
      const saved = await axios.post(`${API}/welding/robots`, {
        site_id: siteId, entry_date: entryDate, robots: Number(robots), shift,
      }).then((r) => r.data);
      toast.success(`${saved.robots} robot(s) saved for shift ${saved.shift} on ${saved.entry_date}`);
      setRobots("");
      await loadReport(siteId, dateFrom, dateTo);
    } catch (e) {
      toast.error(registerError(e).message);
    } finally {
      setWorking(false);
    }
  };

  const pickItem = async (product) => {
    setPicked(product);
    setMslValue("");
    setMslUom("");
    if (!master.sap_msl_field) {
      // SAP does not carry SFG MSL on any integration service yet, so there is
      // nothing to ask it for - say so instead of spinning.
      setMslNote("SAP's SFG MSL (Planning tab) is not released to the integration services yet - ask your SAP key user to publish the field.");
      return;
    }
    setMslNote(`reading ${master.sap_msl_field} from the SAP item master...`);
    try {
      const found = await axios
        .get(`${API}/welding/msl-lookup`, { params: { product_id: product.product_id, site_id: siteId || undefined } })
        .then((r) => r.data);
      if (found.msl_per_robot) setMslValue(String(found.msl_per_robot));
      setMslUom(found.uom || "");
      setMslNote(found.message || "");
    } catch (e) {
      setMslNote(registerError(e).message);
    }
  };

  const saveItem = async () => {
    setWorking(true);
    try {
      await axios.post(`${API}/welding/master`, {
        product_id: picked.product_id, description: picked.description, uom: picked.uom, site_id: siteId || undefined,
      });
      toast.success(`${picked.product_id} set at ${mslValue} ${mslUom || picked.uom || ""} per robot from SAP`);
      setPicked(null);
      setMslValue("");
      setMslUom("");
      setMslNote("");
      const m = await axios.get(`${API}/welding/master`).then((r) => r.data);
      setMaster(m);
      await loadReport(siteId, dateFrom, dateTo);
    } catch (e) {
      toast.error(registerError(e).message);
    } finally {
      setWorking(false);
    }
  };

  const removeItem = async (productId) => {
    setWorking(true);
    try {
      await axios.delete(`${API}/welding/master/${encodeURIComponent(productId)}`);
      toast.success(`${productId} removed`);
      const m = await axios.get(`${API}/welding/master`).then((r) => r.data);
      setMaster(m);
      await loadReport(siteId, dateFrom, dateTo);
    } catch (e) {
      toast.error(registerError(e).message);
    } finally {
      setWorking(false);
    }
  };

  const applyRange = async (from, to) => {
    setDateFrom(from);
    setDateTo(to);
    await loadReport(siteId, from, to);
  };

  if (loading) {
    return (
      <div className="py-16 text-center" data-testid="welding-loading">
        <CircleNotch size={24} weight="bold" className="animate-spin text-[#0E7C86] mx-auto" />
      </div>
    );
  }

  const sites = [...new Set((master.departments || []).map((d) => d.site_id))];
  const department = (master.departments || []).find((d) => d.site_id === siteId);
  const warehouseId = department?.warehouse_id || (siteId ? `${siteId}-WEL` : "");
  const exportUrl = `${API}/welding/report/export?${new URLSearchParams({
    ...(siteId ? { site_id: siteId } : {}), date_from: dateFrom, date_to: dateTo,
  }).toString()}`;

  return (
    <div className="space-y-4" data-testid="welding-panel">
      {sites.length === 0 && (
        <div className="bg-[#FFFAEB] border border-[#FEDF89] rounded-xl p-3 text-[13px] text-[#B54708]" data-testid="welding-no-department">
          No welding department yet. Create it under Departments with its SAP cost centre (for example
          <span className="font-mono font-bold"> P9-WEL</span>) - if SAP has it but the list does not, press
          <strong> Refresh cost centres from SAP</strong> on the Setup tab first.
        </div>
      )}

      <div className="bg-white border border-[#E4E7EC] rounded-xl p-4" data-testid="welding-robot-entry">
        <div className="flex items-center gap-2 mb-1">
          <Robot size={16} weight="fill" className="text-[#0E7C86]" />
          <h2 className="font-heading text-base font-bold text-[#101828]">Running robots</h2>
        </div>
        <p className="text-[12px] text-[#667085] mb-3">
          Save how many welding robots are running on the shift. Each shift can be set once, for today only - after
          that only an administrator can change it or enter a day gone by, and every change is kept with the name
          behind it. The warehouse follows the site on its own: P1 → P1-WEL, P2 → P2-WEL.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-[120px]">
            <Label className="text-[11px] font-bold text-[#344054]">Site</Label>
            {lockedSite ? (
              <div
                className="h-9 mt-1 flex items-center px-3 rounded-md border border-[#D0D5DD] bg-[#F9FAFB] text-sm font-bold text-[#101828]"
                data-testid="welding-site-locked"
              >
                {lockedSite}
              </div>
            ) : (
              <Select value={siteId} onValueChange={(v) => { setSiteId(v); loadReport(v, dateFrom, dateTo); }}>
                <SelectTrigger className="h-9 mt-1" data-testid="welding-site-select">
                  <SelectValue placeholder="Site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`welding-site-option-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="w-[150px]">
            <Label className="text-[11px] font-bold text-[#344054]">Warehouse</Label>
            <div
              className="h-9 mt-1 flex items-center px-3 rounded-md border border-[#D0D5DD] bg-[#F9FAFB] font-mono text-sm font-bold text-[#101828]"
              title="The welding godown of this site - filled in from the site"
              data-testid="welding-warehouse"
            >
              {warehouseId || "—"}
            </div>
          </div>
          <div className="w-[110px]">
            <Label className="text-[11px] font-bold text-[#344054]">Shift</Label>
            <Select value={shift} onValueChange={setShift}>
              <SelectTrigger className="h-9 mt-1" data-testid="welding-shift-select">
                <SelectValue placeholder="Shift" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A" data-testid="welding-shift-option-A">Shift A</SelectItem>
                <SelectItem value="B" data-testid="welding-shift-option-B">Shift B</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-[150px]">
            <Label className="text-[11px] font-bold text-[#344054]">Date</Label>
            <Input
              type="date"
              value={entryDate}
              min={isAdmin ? undefined : today()}
              max={isAdmin ? undefined : today()}
              onChange={(e) => setEntryDate(e.target.value)}
              className="h-9 mt-1"
              data-testid="welding-date-input"
            />
          </div>
          <div className="w-[130px]">
            <Label className="text-[11px] font-bold text-[#344054]">Running robots</Label>
            <Input
              type="number"
              min="0"
              placeholder="10"
              value={robots}
              onChange={(e) => setRobots(e.target.value)}
              className="h-9 mt-1"
              data-testid="welding-robots-input"
            />
          </div>
          <Button
            className="h-9 bg-[#0E7C86] hover:bg-[#0B6B74]"
            disabled={working || !siteId || !entryDate || robots === ""}
            onClick={saveRobots}
            data-testid="welding-robots-save"
          >
            {working ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="bold" />}
            Save
          </Button>
        </div>
        {department && !department.warehouse_in_sap && (
          <p className="mt-2 text-[11px] text-[#B54708]" data-testid="welding-warehouse-note">
            SAP has not reported <span className="font-mono font-bold">{warehouseId}</span> as a godown yet - it is
            taken from the site name. If SAP calls the welding godown something else, map the welding department to
            that logistics area under Departments and this follows it.
          </p>
        )}
      </div>

      <div className="bg-white border border-[#E4E7EC] rounded-xl p-4" data-testid="welding-msl-master">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
          <h2 className="font-heading text-base font-bold text-[#101828]">MSL per robot</h2>
          {master.sap_msl_field ? (
            <span className="text-[11px] font-bold text-[#027A48]" data-testid="welding-msl-source">
              reading SFG MSL from the SAP item master{siteId ? ` (planning area ${siteId})` : ""}
            </span>
          ) : (
            <span className="text-[11px] text-[#B54708]" data-testid="welding-msl-source">
              SAP&apos;s SFG MSL (Planning tab) is not released to the planning service yet - ask your SAP key user to publish it
            </span>
          )}
        </div>
        <p className="text-[12px] text-[#667085] mb-3">
          What one robot needs of each welding material, read from the item's Planning tab in SAP - it is never typed
          here. The report multiplies it by the robots running that shift: 10 robots × 250 kg = 2,500 kg.
        </p>
        {isAdmin && (
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <div className="w-[320px]">
              <Label className="text-[11px] font-bold text-[#344054]">Item</Label>
              {picked ? (
                <div className="h-9 mt-1 flex items-center px-3 rounded-md border border-[#0E7C86] bg-[#F0FDFA] text-sm" data-testid="welding-item-selected">
                  <span className="font-mono font-bold text-[#101828] mr-2">{picked.product_id}</span>
                  <span className="text-[#667085] truncate">{picked.description}</span>
                  <button onClick={() => setPicked(null)} className="ml-auto text-[#667085] hover:text-[#B42318] pl-2" data-testid="welding-item-clear">✕</button>
                </div>
              ) : <ItemPicker onPick={pickItem} />}
            </div>
            <div className="w-[190px]">
              <Label className="text-[11px] font-bold text-[#344054]">
                MSL per robot {mslUom || picked?.uom ? `(${mslUom || picked.uom})` : ""} · from SAP
              </Label>
              <div
                className="h-9 mt-1 flex items-center justify-end px-3 rounded-md border border-[#D0D5DD] bg-[#F9FAFB] text-sm font-bold tabular-nums text-[#101828]"
                title="SAP's SFG MSL for this item - maintained on the item's Planning tab, not here"
                data-testid="welding-msl-input"
              >
                {mslValue || "—"}
              </div>
            </div>
            <Button
              className="h-9 bg-[#0E7C86] hover:bg-[#0B6B74]"
              disabled={working || !picked || !mslValue}
              onClick={saveItem}
              data-testid="welding-msl-save"
            >
              <Plus size={14} weight="bold" /> Save item
            </Button>
            {mslNote && (
              <p className="basis-full text-[11px] text-[#475467]" data-testid="welding-msl-lookup-note">{mslNote}</p>
            )}
          </div>
        )}
        {(master.items || []).length === 0 ? (
          <p className="text-[13px] text-[#667085]" data-testid="welding-msl-empty">No welding item mapped yet.</p>
        ) : (
          <div className="border border-[#D0D5DD] rounded-sm overflow-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="welding-msl-table">
              <thead>
                <tr>
                  {["Item", "Description", "MSL per Robot", "Unit", "Source", ""].map((h) => (
                    <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-[11px] font-bold text-[#344054] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {master.items.map((i) => (
                  <tr key={i.product_id} data-testid={`welding-msl-row-${i.product_id}`}>
                    <td className="border border-[#D0D5DD] p-1.5 font-mono font-bold text-[#101828]">{i.product_id}</td>
                    <td className="border border-[#D0D5DD] p-1.5 text-xs text-[#475467]">{i.description || "—"}</td>
                    <td className="border border-[#D0D5DD] p-1.5 text-right tabular-nums font-bold">{qty(i.msl_per_robot)}</td>
                    <td className="border border-[#D0D5DD] p-1.5 text-xs">{i.uom || "—"}</td>
                    <td className="border border-[#D0D5DD] p-1.5 text-xs">
                      {i.source === "sap" ? <span className="text-[#027A48] font-bold">SAP item master</span> : <span className="text-[#667085]">entered by {i.updated_by || "admin"}</span>}
                    </td>
                    <td className="border border-[#D0D5DD] p-1.5 w-10">
                      {isAdmin ? (
                        <button onClick={() => removeItem(i.product_id)} disabled={working} className="text-[#B42318] disabled:opacity-40" data-testid={`welding-msl-remove-${i.product_id}`}>
                          <Trash size={13} weight="bold" />
                        </button>
                      ) : <Lock size={12} className="text-[#98A2B3]" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E4E7EC] rounded-xl p-4" data-testid="welding-report">
        <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
          <div>
            <h2 className="font-heading text-base font-bold text-[#101828]">Welding report</h2>
            <p className="text-[12px] text-[#667085]">Shift by shift: the robots that ran and the MSL they need.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[11px] font-bold text-[#344054]">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => applyRange(e.target.value, dateTo)} className="h-9 mt-1 w-[150px]" data-testid="welding-report-from" />
            </div>
            <div>
              <Label className="text-[11px] font-bold text-[#344054]">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => applyRange(dateFrom, e.target.value)} className="h-9 mt-1 w-[150px]" data-testid="welding-report-to" />
            </div>
            <Button asChild variant="outline" className="h-9 border-[#0E7C86] text-[#0E7C86]" data-testid="welding-report-export">
              <a href={exportUrl}><DownloadSimple size={14} weight="bold" /> Excel</a>
            </Button>
          </div>
        </div>
        {(report.days || []).length === 0 ? (
          <p className="text-[13px] text-[#667085]" data-testid="welding-report-empty">Nothing saved in this range yet.</p>
        ) : (
          <div className="border border-[#D0D5DD] rounded-sm overflow-auto">
            <table className="w-full text-[13px] border-collapse" data-testid="welding-report-table">
              <thead>
                <tr>
                  {["Date", "Site", "Warehouse", "Shift", "Robots", "Item", "Description",
                    "MSL / Robot", "MSL Qty", "Unit", "Created By", "Last Saved By"].map((h) => (
                    <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-[11px] font-bold text-[#344054] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.days.flatMap((d) => {
                  const head = (
                    <>
                      <td className="border border-[#D0D5DD] p-1.5 whitespace-nowrap">{d.entry_date}</td>
                      <td className="border border-[#D0D5DD] p-1.5">{d.site_id}</td>
                      <td className="border border-[#D0D5DD] p-1.5 font-mono text-xs">{d.warehouse_id}</td>
                      <td className="border border-[#D0D5DD] p-1.5 text-center font-bold">{d.shift || "—"}</td>
                      <td className="border border-[#D0D5DD] p-1.5 text-right tabular-nums font-bold text-[#0E7C86]">{d.robots}</td>
                    </>
                  );
                  const tail = (
                    <>
                      <td className="border border-[#D0D5DD] p-1.5 text-xs text-[#475467] whitespace-nowrap">{d.created_by || "—"}</td>
                      <td className="border border-[#D0D5DD] p-1.5 text-xs text-[#475467] whitespace-nowrap">
                        {d.updated_by || "—"}{d.changes > 1 ? ` (changed ${d.changes - 1}x)` : ""}
                      </td>
                    </>
                  );
                  const key = `${d.entry_date}-${d.shift || "x"}-${d.site_id}`;
                  if (d.items.length === 0) {
                    return [(
                      <tr key={key} data-testid={`welding-report-row-${key}`}>
                        {head}
                        <td className="border border-[#D0D5DD] p-1.5 text-xs text-[#667085]" colSpan={5}>No welding item has an MSL yet</td>
                        {tail}
                      </tr>
                    )];
                  }
                  return d.items.map((i) => (
                    <tr key={`${key}-${i.product_id}`} data-testid={`welding-report-row-${key}-${i.product_id}`}>
                      {head}
                      <td className="border border-[#D0D5DD] p-1.5 font-mono font-bold text-[#101828]">{i.product_id}</td>
                      <td className="border border-[#D0D5DD] p-1.5 text-xs text-[#475467]">{i.description || "—"}</td>
                      <td className="border border-[#D0D5DD] p-1.5 text-right tabular-nums">{qty(i.msl_per_robot)}</td>
                      <td className="border border-[#D0D5DD] p-1.5 text-right tabular-nums font-bold text-[#0E7C86]">{qty(i.msl_quantity)}</td>
                      <td className="border border-[#D0D5DD] p-1.5 text-xs">{i.uom || "—"}</td>
                      {tail}
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
